import { Sparkles } from "lucide-react";
import { playMoodSound } from "@/utils/sounds";
import { MOOD_EMOJIS } from "@/utils/constants";
import { useTheme } from "@/context/ThemeContext";

// Build a lookup map from the MOOD_EMOJIS constant
const getMoodData = (moodValue) => {
  const found = MOOD_EMOJIS.find(m => m.value === moodValue || m.value === moodValue?.toLowerCase());
  if (found) {
    return {
      emoji: found.emoji,
      label: found.label,
      isCustom: found.isCustom || false,
      imageSrc: found.imageSrc || null
    };
  }
  // Fallback for unknown moods
  return { emoji: '😊', label: moodValue?.replace(/_/g, ' ') || 'Unknown', isCustom: false };
};

// Animated gradient glow for UnCloned mood
const UnClonedMoodIcon = ({ size = 'w-8 h-8' }) => (
  <>
    <style>{`
      @keyframes uncloned-gradient {
        0%, 100% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
      }
      .uncloned-glow {
        animation: uncloned-gradient 3s ease infinite;
      }
    `}</style>
    <div 
      className="w-8 h-8 uncloned-glow"
      style={{
        background: 'linear-gradient(90deg, var(--theme-primary) 0%, var(--theme-secondary) 25%, #D0FF0B 50%, #0FFFE2 75%, var(--theme-primary) 100%)',
        backgroundSize: '200% 100%',
        WebkitMaskImage: 'url("/mood-uncloned.png")',
        WebkitMaskSize: 'contain',
        WebkitMaskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
        maskImage: 'url("/mood-uncloned.png")',
        maskSize: 'contain',
        maskRepeat: 'no-repeat',
        maskPosition: 'center',
        filter: 'drop-shadow(0 0 8px rgba(var(--theme-primary-rgb), 0.5)) drop-shadow(0 0 16px rgba(var(--theme-secondary-rgb), 0.3))',
      }}
    />
  </>
);

// Custom branded mood icon for B2B clients
const CustomBrandedMoodIcon = ({ imageUrl }) => (
  <img 
    src={imageUrl} 
    alt="Custom mood" 
    className="w-8 h-8 object-contain"
    style={{
      filter: 'drop-shadow(0 0 4px rgba(var(--theme-primary-rgb), 0.3))',
    }}
  />
);

const MoodTracker = ({ selectedMood, onMoodSelect, customMood }) => {
  const { getEmoji } = useTheme();
  
  // Build mood list - replace first item (UnCloned) with custom mood if provided
  const getMoodList = () => {
    if (customMood?.enabled && customMood?.imageUrl && customMood?.label) {
      // Replace the first mood (UnCloned) with custom branded mood
      const customMoodItem = {
        emoji: "custom",
        label: customMood.label,
        value: "custom_branded",
        isCustomBranded: true,
        imageUrl: customMood.imageUrl
      };
      return [customMoodItem, ...MOOD_EMOJIS.slice(1)];
    }
    return MOOD_EMOJIS;
  };
  
  const moodList = getMoodList();
  
  const handleMoodClick = (mood) => {
    playMoodSound(mood.value);
    onMoodSelect(mood);
  };

  return (
    <div 
      className="glass-card p-6" 
      data-testid="mood-tracker"
    >
      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
        <Sparkles className="w-5 h-5" style={{ color: 'var(--theme-primary)' }} />
        <span style={{ color: 'var(--theme-text)' }}>How are you feeling?</span>
      </h3>
      <div className="mood-grid">
        {moodList.map((mood) => (
          <button
            key={mood.value}
            onClick={() => handleMoodClick(mood)}
            className={`p-3 rounded-xl transition-all flex flex-col items-center gap-1 ${
              selectedMood?.value === mood.value
                ? "bg-gradient-to-br from-[var(--theme-primary)] to-[var(--theme-secondary)] sparkle-emoji selected"
                : "bg-[var(--theme-background)] hover:bg-[var(--theme-background-secondary)] sparkle-emoji"
            }`}
            data-testid={`mood-${mood.value}`}
            title={mood.label}
          >
            {mood.isCustomBranded ? (
              <CustomBrandedMoodIcon imageUrl={mood.imageUrl} />
            ) : mood.isCustom ? (
              <UnClonedMoodIcon />
            ) : (
              <span className="text-3xl">{getEmoji(mood.emoji)}</span>
            )}
            <span className="text-xs opacity-80" style={{ color: 'var(--theme-text)' }}>{mood.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default MoodTracker;
